import { type ColumnDef } from "@tanstack/react-table";
import { type QuestionnaireOverviewData } from "../../types";
import { FlexRow } from "@design-system/Layout/Flex";
import { Ellipsis } from "@design-system/Typography/Ellipsis";
import { Text } from "@design-system/Typography/Text";
import { nameNormalized } from "@app/shared/utils/tableSortingFns";
import CheckboxCell from "@app/components/Table/CheckboxCell";
import { CheckboxHeader } from "@design-system/Table";
import { useInactiveQuestionsModalTranslations } from "./i18n";

const selectCol: ColumnDef<QuestionnaireOverviewData[number]> = {
    id: "select",
    header: ({ table }) => {
        return <CheckboxHeader table={table} />;
    },
    cell: ({ row }) => <CheckboxCell row={row} />,
    meta: {
        headerClassName: "w-[50px] max-w-[50px]",
    },
};

const nameColumn: ColumnDef<QuestionnaireOverviewData[number]> = {
    accessorKey: "name",
    sortingFn: nameNormalized,
    id: "question",
    header: () => {
        const { t, keys } = useInactiveQuestionsModalTranslations();
        return (
            <Text className="text-secondary" variant="body-sm-bold">
                {t(keys.table_header_name)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const text = row.original.name;

        return (
            <FlexRow gap="2" alignItems="center">
                <Ellipsis
                    lineClamp={1}
                    hasTooltip
                    asChild
                    className={"cursor-pointer"}
                >
                    <Text> {text}</Text>
                </Ellipsis>
            </FlexRow>
        );
    },
    meta: {
        cellClassName: "max-w-[700px] min-w-[700px]",
        headerClassName: "max-w-[700px] min-w-[700px]",
        colWidth: "700px",
    },
};

const groupColumn: ColumnDef<QuestionnaireOverviewData[number]> = {
    accessorKey: "group",
    sortingFn: nameNormalized,
    id: "group",
    header: () => {
        const { t, keys } = useInactiveQuestionsModalTranslations();
        return (
            <Text className="text-secondary" variant="body-sm-bold">
                {t(keys.table_header_group)}
            </Text>
        );
    },
    cell: ({ row }) => {
        const text = row.original.group?.name;

        return (
            <FlexRow gap="2" alignItems="center">
                <Ellipsis
                    lineClamp={2}
                    hasTooltip
                    asChild
                    className={"cursor-pointer"}
                >
                    <Text> {text ?? ""}</Text>
                </Ellipsis>
            </FlexRow>
        );
    },
    meta: {
        cellClassName: "max-w-[200px]",
        headerClassName: "max-w-[200px]",
        colWidth: "200px",
    },
};

export const useColumns = (): ColumnDef<
    QuestionnaireOverviewData[number]
>[] => {
    return [selectCol, groupColumn, nameColumn];
};
